import { ReactNode, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"

import Button from "~/components/Button/Button"
import { getUser, setUser, createAlert } from "~/store"
import Api from "~/utils/Api"

import "./McpConnectionPanel.scss"

const MASKED_TOKEN = "•".repeat(32)

function CopyButton({ text }: { text: string }) {
  const dispatch = useDispatch()
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    }
  }, [])

  const copy = () => {
    if (!navigator.clipboard) {
      dispatch(
        createAlert({
          message: "Clipboard access not available",
          type: "error",
        }),
      )
      return
    }

    navigator.clipboard.writeText(text).then(() => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
      setCopied(true)
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Button
      text={copied ? "Copied" : "Copy"}
      style="outline"
      size="small"
      onClick={copy}
    />
  )
}

type FieldProps = {
  label: string
  value: string
  copyText: string
  wrap?: boolean
  inlineAction?: ReactNode
}

function Field({
  label,
  value,
  copyText,
  wrap = false,
  inlineAction,
}: FieldProps) {
  return (
    <div className="McpConnectionPanel__field">
      <span className="McpConnectionPanel__label">{label}</span>
      <div className="McpConnectionPanel__value-row">
        <div className="McpConnectionPanel__value">
          <code
            className={classNames("McpConnectionPanel__text", {
              "McpConnectionPanel__text--wrap": wrap,
            })}
          >
            {value}
          </code>
          {inlineAction}
        </div>
        <CopyButton text={copyText} />
      </div>
    </div>
  )
}

export default function McpConnectionPanel() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const [showToken, setShowToken] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const mcpUrl = `${window.location.origin}/mcp`
  const token = user?.mcp_token || ""
  const visibleToken = showToken ? token : MASKED_TOKEN
  const connectCommand = (bearer: string, separator = " ") =>
    `claude mcp add --transport http pomodoro ${mcpUrl}${separator}--header "Authorization: Bearer ${bearer}"`

  const regenerateToken = () => {
    setRegenerating(true)

    Api.post("/users/regenerate_mcp_token")
      .then((response) => {
        dispatch(setUser(response.data["data"]["attributes"]))
        dispatch(
          createAlert({
            message: token ? "MCP token regenerated" : "MCP token generated",
            type: "success",
          }),
        )
      })
      .catch((error) => {
        dispatch(createAlert({ message: error.message, type: "error" }))
      })
      .then(() => {
        setRegenerating(false)
      })
  }

  if (!token) {
    return (
      <div className="McpConnectionPanel">
        <p className="McpConnectionPanel__intro">
          Generate a personal token to connect your assistant.
        </p>
        <div>
          <Button
            text={regenerating ? "Generating..." : "Generate token"}
            size="small"
            onClick={regenerateToken}
            disabled={regenerating}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="McpConnectionPanel">
      <p className="McpConnectionPanel__intro">
        Add this server to your assistant, then ask it to &ldquo;start a 25
        minute pomodoro&rdquo; or &ldquo;should I take a break?&rdquo;
      </p>

      <Field label="Server URL" value={mcpUrl} copyText={mcpUrl} />

      <Field
        label="Token"
        value={visibleToken}
        copyText={token}
        inlineAction={
          <button
            type="button"
            className="McpConnectionPanel__reveal"
            onClick={() => setShowToken(!showToken)}
          >
            {showToken ? "Hide" : "Show"}
          </button>
        }
      />

      <Field
        label="Claude Code"
        value={connectCommand(visibleToken, " \\\n  ")}
        copyText={connectCommand(token)}
        wrap
      />

      <div className="McpConnectionPanel__footer">
        <p className="McpConnectionPanel__hint">
          Keep your token secret. Regenerating it disconnects any assistant
          using the current one.
        </p>
        <Button
          text={regenerating ? "Regenerating..." : "Regenerate token"}
          style="outline"
          size="small"
          onClick={regenerateToken}
          disabled={regenerating}
        />
      </div>
    </div>
  )
}
